/**
 * Ficheiros da app (rota `files` do host, multipart, até 20 MB). O proxy REST
 * das funções não passa binários — as funções só recebem as chaves.
 */
import type { AppShell } from "@/App"

type Res<T> = { success: true; data: T } | { success: false; error: string }

export async function uploadAppFile(shell: AppShell, file: Blob, name: string): Promise<string> {
  const form = new FormData()
  form.append("file", file, name)
  const res = await shell.client.post<Res<{ key: string }>>("files", undefined, { body: form })
  if (!res.success) throw new Error(res.error || "Falha a enviar o ficheiro")
  return res.data.key
}

/** URL temporário (5 min) para ver um ficheiro da app. */
export async function appFileUrl(shell: AppShell, key: string): Promise<string> {
  const res = await shell.client.get<Res<{ url: string }>>(`files?key=${encodeURIComponent(key)}`)
  if (!res.success) throw new Error(res.error || "Ficheiro indisponível")
  return res.data.url
}

/**
 * Foto reduzida para o Odoo: lado maior até 2000 px, JPEG 0,82. Os formatos que
 * o browser não descodifica (HEIC no Chrome) e as imagens já pequenas seguem
 * como estão.
 */
export async function shrinkPhoto(file: File, maxSide = 2000): Promise<{ blob: Blob; name: string; mimetype: string }> {
  const keep = { blob: file as Blob, name: file.name, mimetype: file.type || "image/jpeg" }
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return keep
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height))
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return keep
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.82))
    if (!blob || blob.size >= file.size) return keep
    return { blob, name: file.name.replace(/\.[^.]+$/, "") + ".jpg", mimetype: "image/jpeg" }
  } catch {
    return keep
  }
}
